// @ts-check
/**
 * Video Messages API — teacher clips pinned to a student's dashboard.
 *
 * @typedef {import('../../../shared/api/types').VideoMessagesResponse} VideoMessagesResponse
 * @typedef {import('../../../shared/api/types').VideoMessageDoneResponse} VideoMessageDoneResponse
 * @typedef {import('../../../shared/api/types').VideoMessageWatchedResponse} VideoMessageWatchedResponse
 * @typedef {import('../../../shared/api/types').VideoMessageOpenedResponse} VideoMessageOpenedResponse
 * @typedef {import('../../../shared/api/types').VideoMessageCreateResponse} VideoMessageCreateResponse
 * @typedef {import('../../../shared/api/types').ClassVideoMessagesResponse} ClassVideoMessagesResponse
 * @typedef {import('../../../shared/api/types').VideoMessageRetractResponse} VideoMessageRetractResponse
 */

import { apiRequest } from '../../../shared/api/base';

/**
 * Messages waiting for this student (unwatched, un-retracted).
 * @param {string} studentId
 * @returns {Promise<VideoMessagesResponse>}
 */
export function getVideoMessages(studentId) {
  return apiRequest(`/students/${studentId}/video-messages`);
}

/**
 * The kid opened the player. Counter only — opening is not watching.
 * @param {string} studentId
 * @param {number} messageId
 * @returns {Promise<VideoMessageOpenedResponse>}
 */
export function markVideoMessageOpened(studentId, messageId) {
  return apiRequest(`/students/${studentId}/video-messages/${messageId}/opened`, {
    method: 'POST',
  });
}

/**
 * A playthrough reached the end. Counts every time, so replays register as
 * rewatches. The card stays either way — clearing it is a separate action.
 * @param {string} studentId
 * @param {number} messageId
 * @returns {Promise<VideoMessageWatchedResponse>}
 */
export function markVideoMessageWatched(studentId, messageId) {
  return apiRequest(`/students/${studentId}/video-messages/${messageId}/watched`, {
    method: 'POST',
  });
}

/**
 * The kid cleared the card.
 * @param {string} studentId
 * @param {number} messageId
 * @returns {Promise<VideoMessageDoneResponse>}
 */
export function markVideoMessageDone(studentId, messageId) {
  return apiRequest(`/students/${studentId}/video-messages/${messageId}/done`, {
    method: 'POST',
  });
}

/**
 * Teacher: send one clip to one or more students.
 * @param {{studentIds: string[], videoUrl: string, title?: string, classId?: number|null}} payload
 * @returns {Promise<VideoMessageCreateResponse>}
 */
export function sendVideoMessage(payload) {
  return apiRequest('/video-messages', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * Teacher: retract the whole send this message belongs to.
 * @param {number} messageId
 * @returns {Promise<VideoMessageRetractResponse>}
 */
export function retractVideoMessage(messageId) {
  return apiRequest(`/video-messages/${messageId}`, { method: 'DELETE' });
}

/**
 * Teacher: sends for a class, with who has watched.
 * @param {number} classId
 * @returns {Promise<ClassVideoMessagesResponse>}
 */
export function getClassVideoMessages(classId) {
  return apiRequest(`/classes/${classId}/video-messages`);
}
